import * as Sentry from '@sentry/browser'
import { IdTokenResult, User } from 'firebase/auth'
import { SubscriptionStatus, InferredPollingState, SurveyStatus, BillingCycle, PostSignupPhoneStatus } from './auth-jwt-schema'
import { daysUntilFromUnix } from '@/util/time'
import { getOrganization } from '../../organizations/fetch/get-organization'
import { ClientOrganizationRead } from '@/organizations/schemas'

export class UserAccountData {
  private tokenResult: IdTokenResult
  private user: User
  orgData: ClientOrganizationRead | null = null

  constructor(user: User, tokenResult: IdTokenResult, orgData: ClientOrganizationRead | null = null) {
    this.user = user
    this.tokenResult = tokenResult
    this.orgData = orgData
  }

  // Factory async constructor that allows the implementation to await the idTokenResult fetching
  // Always forces a token refresh
  static async create(user: User): Promise<UserAccountData | null> {
    try {
      const tokenResult = await user.getIdTokenResult(true)

      // If the user has an organization_id, then fetch the orgData
      const organizationId = (tokenResult?.claims?.organization_id as string) ?? null

      // getOrganization() resolves to null on any transient fetch/parse failure. We intentionally
      // build account data with orgData=null here rather than failing the whole create(): the
      // initial-load and subscription-poll callers treat a null create() as a hard auth error, so
      // failing here would lock an org member out on one transient hiccup. The refresh path instead
      // detects an org member with missing org data and keeps the last good account data, which is
      // where a mid-session expiry is reflected without reopening the dashboard.
      const orgData = organizationId ? await getOrganization() : null

      return new UserAccountData(user, tokenResult, orgData)
    } catch (error) {
      Sentry.captureException(new Error(`Error Constructing UserAccountData`), {
        extra: {
          error: error,
          errorStringified: JSON.stringify(error),
          user: JSON.stringify(user),
        },
      })

      return null // or handle the error as appropriate for your application
    }
  }

  //============================================================================
  // GETTERS
  //============================================================================
  get isAnonymous(): boolean {
    return this.user.isAnonymous
  }

  get inferredPollingState(): InferredPollingState {
    if (this.user.isAnonymous) {
      return InferredPollingState.NONE
    }

    if (this.subscriptionStatus === SubscriptionStatus.NONE) {
      return InferredPollingState.POLL_SUBSCRIPTION
    }

    if (
      this.subscriptionStatus === SubscriptionStatus.PRE_TRIAL ||
      this.subscriptionStatus === SubscriptionStatus.EXPIRED ||
      this.subscriptionStatus === SubscriptionStatus.DELETED
    ) {
      return InferredPollingState.POLL_PAYMENT
    }

    return InferredPollingState.NONE
  }

  get surveyCompleted(): boolean {
    return this.tokenResult.claims.surveyStatus === SurveyStatus.COMPLETED
  }

  get postSignupPhoneStatus(): PostSignupPhoneStatus | null {
    const status = this.tokenResult.claims.post_signup_phone_status
    return Object.values(PostSignupPhoneStatus).includes(status as PostSignupPhoneStatus) ? (status as PostSignupPhoneStatus) : null
  }

  get isOrgAccount(): boolean {
    return this.tokenResult.claims.organization_id ? true : false
  }

  /**
   * Whether this is an organization member whose organization data could not be loaded.
   *
   * getOrganization() resolves to null on a transient fetch/parse miss, so create() can return an
   * org member with orgData == null (it intentionally fails open rather than locking every org
   * member out on one hiccup). In that state the subscription getters below fall back to the
   * member's individual JWT claim, which can still read as a valid trial/paid subscription and admit
   * them past an expired org. Callers (SubscriptionGuard) must treat this as "hold pending" — show a
   * loader rather than trusting validSubscription/invalidSubscription. The account-data refresh in
   * root.tsx retries create() and resolves to real access or OrgAccessDeniedPage.
   */
  get orgDataUnavailable(): boolean {
    return this.isOrgAccount && this.orgData === null
  }

  get isOrgAdmin(): boolean {
    return this.isOrgAccount && this.tokenResult.claims.role === 'admin'
  }

  /**
   * Whether the user is the payer for their organization.
   *
   * Identifies the org member who holds the Stripe subscription and can make changes on the stripe
   * billing portal to manage the subscription and billing settings on behalf of the organization.
   */
  get isOrgPayer(): boolean {
    return this.isOrgAccount && Boolean(this.orgData?.is_organization_payer)
  }

  get legacyDriveCompatibleOrganizationId(): string | null {
    return (this.tokenResult.claims.organization_id as string) ?? null
  }

  get role(): string | null {
    return (this.tokenResult.claims.role as string) ?? null
  }

  // Get the user's subscription status
  // Initialize as .NONE
  get subscriptionStatus(): SubscriptionStatus {
    // If the user has a orgData, just return organization
    if (this.orgData) return SubscriptionStatus.ORGANIZATION

    const subscriptionStatus = this.tokenResult.claims.subscription_status
    if (Object.values(SubscriptionStatus).includes(subscriptionStatus as SubscriptionStatus)) {
      return subscriptionStatus as SubscriptionStatus
    } else {
      return SubscriptionStatus.NONE
    }
  }

  // Get the display name for the given SubscriptionStatus
  get subscriptionStatusDisplayName(): string {
    switch (this.subscriptionStatus) {
      case SubscriptionStatus.PAID:
        return 'Paid'
      case SubscriptionStatus.TRIAL:
        return 'Trial'
      case SubscriptionStatus.PARTNER:
        return 'Partner'
      case SubscriptionStatus.PAXTON:
        return 'Paxton'
      case SubscriptionStatus.EXPIRED:
        return 'Expired'
      case SubscriptionStatus.DELETED:
        return 'Deleted'
      case SubscriptionStatus.ORGANIZATION:
        return 'Organization'
      case SubscriptionStatus.PRE_TRIAL:
        return 'Pre-Trial'
      case SubscriptionStatus.NONE:
      default:
        return 'Not available'
    }
  }

  // Get pre-determined subscription states useful for UX rendering
  // When the user has a valid subscription
  get validSubscription(): boolean {
    return (
      // User is in an org that has access
      this.orgData?.has_access === true ||
      // Individual users with a valid trialing or paid subscription
      this.subscriptionStatus === SubscriptionStatus.TRIAL ||
      this.subscriptionStatus === SubscriptionStatus.PAID ||
      // Special accounts where access is granted without an actual Stripe subscription
      this.subscriptionStatus === SubscriptionStatus.PARTNER ||
      this.subscriptionStatus === SubscriptionStatus.PAXTON
    )
  }

  /**
   * Whether the user is exempt from requiring a Stripe subscription.
   *
   * These accounts bypass Stripe billing entirely - they gain access without creating or managing a subscription.
   * This flag identifies those accounts so we can avoid showing irrelevant subscription/billing UI or logic.
   */
  get isSubscriptionExempt(): boolean {
    return [SubscriptionStatus.PARTNER, SubscriptionStatus.PAXTON].includes(this.subscriptionStatus)
  }

  /**
   * Whether the user can view Stripe subscription information such as billing details for their account.
   *
   * Rules:
   * - User must be logged in to view stripe subscription info
   * - Single-user accounts: TRIAL or PAID -> can view
   * - Organization accounts: any org admin (including the org creator) -> can view
   */
  get canViewStripeSubscriptionInfo(): boolean {
    // User not logged in
    if (!this.user || this.user.isAnonymous) return false

    // Individual users with a trialing or paid subscription
    if (this.subscriptionStatus === SubscriptionStatus.TRIAL || this.subscriptionStatus === SubscriptionStatus.PAID) {
      return true
    }

    // Org admins need to view subscription info to understand per-seat cost when inviting members,
    // regardless of whether the org currently has access (e.g., during trial or after expiration)
    if (this.isOrgAdmin && this.orgData) return true

    return false
  }

  // When the status suggests we are still pending a subscription (possibly user is created but not yet subscribed)
  get pendingSubscription(): boolean {
    return this.subscriptionStatus === SubscriptionStatus.NONE
  }

  get pendingPaymentMethod(): boolean {
    return (
      this.subscriptionStatus === SubscriptionStatus.PRE_TRIAL ||
      this.subscriptionStatus === SubscriptionStatus.EXPIRED ||
      this.subscriptionStatus === SubscriptionStatus.DELETED
    )
  }

  get expiredPaymentMethod(): boolean {
    return this.subscriptionStatus === SubscriptionStatus.EXPIRED || this.subscriptionStatus === SubscriptionStatus.DELETED
  }

  // When the status suggests we need to show the paywall
  get invalidSubscription(): boolean {
    return (
      this.orgData?.has_access === false || this.subscriptionStatus === SubscriptionStatus.EXPIRED || this.subscriptionStatus === SubscriptionStatus.DELETED
    )
  }

  get billingCycle(): BillingCycle {
    // existing monthly users will not have a billing cycle set in their claims, so default to monthly
    return (this.tokenResult.claims.billing_cycle as BillingCycle) ?? BillingCycle.MONTHLY
  }

  // Get the number of days until the trial ends
  get trialEnd(): number {
    const trialExp = this.tokenResult.claims.trial_exp

    // Validate that it's a number
    if (typeof trialExp !== 'number') {
      return 0
    }

    return daysUntilFromUnix(trialExp)
  }

  // Get whether the user has a default payment method
  get defaultPayment(): boolean {
    const defaultPayment = this.tokenResult.claims.default_payment

    // If undefined
    if (typeof defaultPayment === 'undefined') {
      return false
    }

    // If not a boolean
    if (typeof defaultPayment !== 'boolean') {
      console.error(`Invalid defaultPayment - not a boolean: ${defaultPayment}`)
      return false
    }

    return defaultPayment
  }

  get isPaxtonUserAccount(): boolean {
    return this.user.email?.endsWith('@paxton.ai') ?? false
  }
}
